import type { Option } from '@/utils/helpers'
import { cx } from '@/utils/utils'
import { useStaffContext } from '@/web-components/StaffApp/StaffAppContext'
import { Icon } from '@components/Icons'
import * as select from '@zag-js/select'
import { normalizeProps, useMachine } from '@zag-js/solid'
import {
  createEffect,
  createMemo,
  createUniqueId,
  For,
  Match,
  onMount,
  Show,
  Switch,
  type Component,
} from 'solid-js'

type ZagSelectProps = {
  options: Option[]
  label?: string
  placeholder: string
  selectedPlaceholder?: string
  multiple?: boolean
  onChange: (e: Option[]) => void
  onProvideClear?: (clearFunc: (value?: string) => void) => void
  defaultValue?: string[]
  selectClass?: string
}
export const ZagSelect: Component<ZagSelectProps> = (props) => {
  const { shadowRoot } = useStaffContext()

  const [state, send] = useMachine(
    select.machine<Option>({
      id: createUniqueId(),
      // eslint-disable-next-line solid/reactivity
      multiple: props.multiple,
      collection: select.collection<Option>({ items: [] }),
      getRootNode: () => shadowRoot()?.getRootNode() || document,
      positioning: {
        // strategy: 'fixed',
        placement: 'bottom-start',
      },
      onValueChange(details) {
        const selectedOptions = details.items as Option[]
        props.onChange(selectedOptions)
      },
      value: props.defaultValue,
    })
  )

  const api = createMemo(() => select.connect(state, send, normalizeProps))

  const checkboxClasses = cx(
    'size-24 rounded-4px border-1px border-gray5 bg-white flex-shrink-0 pointer-events-none'
  )

  // Effect to provide the clear function to the parent
  createEffect(() => {
    if (props.onProvideClear)
      // eslint-disable-next-line solid/reactivity
      props.onProvideClear((v) => {
        api().clearValue(v)
      })
  })

  onMount(() => {
    api().setCollection(select.collection({ items: props.options }))
  })

  return (
    <div class="relative flex items-stretch">
      <div class="w-full">
        <label {...api().getLabelProps()} class="sr-only">
          {props.label}
        </label>
        <button {...api().getTriggerProps()} class={cx([props.selectClass])}>
          <Switch>
            <Match when={props.selectedPlaceholder}>
              <Show
                when={api().value.length === 0}
                fallback={
                  <>
                    <span>{props.selectedPlaceholder}</span>
                  </>
                }
              >
                <span>{props.placeholder}</span>
              </Show>
            </Match>
            <Match when={props.selectedPlaceholder === undefined}>
              <span>{api().valueAsString || 'Select option'}</span>
            </Match>
          </Switch>
          <span>
            <Icon icon="chevronDown" class={'size-12'} />
          </span>
        </button>
      </div>

      <div class="absolute bottom-0 left-0 z-50 translate-y-full">
        <div
          class="rounded-4px border-1px border-blue border-opacity-10 bg-white"
          {...api().getPositionerProps()}
        >
          <ul {...api().getContentProps()}>
            <For each={props.options}>
              {(item) => (
                <li
                  {...api().getItemProps({ item })}
                  class="group cursor-pointer px-15 py-10 hover:bg-blue hover:bg-opacity-10 data-[highlighted]:bg-blue data-[highlighted]:bg-opacity-10"
                >
                  <div class="flex items-center justify-between gap-20 whitespace-nowrap">
                    <span class="type-mobile-p3-medium text-black md:type-p3-medium">
                      {item.label}
                    </span>

                    <input
                      type="checkbox"
                      tabIndex={-1}
                      checked={false}
                      class={cx([
                        checkboxClasses,
                        'hidden group-data-[state=unchecked]:inline-block',
                      ])}
                    />
                    <input
                      type="checkbox"
                      tabIndex={-1}
                      checked={true}
                      class={cx([
                        checkboxClasses,
                        'hidden group-data-[state=checked]:inline-block',
                      ])}
                    />
                  </div>
                </li>
              )}
            </For>
          </ul>
        </div>
      </div>
    </div>
  )
}
